import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setCalendarDate,
  setCustomers,
  setPayoutId,
  setPayouts,
} from "./payoutsSlice";
import { TRootState } from "../../../../store";
import useAxiosClient from "../../../../hooks/useAxiosClient";
import {
  CustomerProfile,
  mapCustomersResponseToModel,
  mapPayoutResponseToModel,
  PayoutDataResponse,
} from "../models/payoutModels";
import { TCalendarType } from "../../../../components/commons/ono-calendar-v2/calendar_types";
import { setPayoutViewId } from "../../payout_view/bloc/payoutViewSlice";
import { getUpdatedPayouts } from "../functions/payouts_functions";
import * as XLSX from "xlsx";
import { getReportFileName } from "../../../reports/functions";
import { toast } from "react-toastify";

const usePayouts = () => {
  const { calendarDate, payouts, adminId, filters, customers } = useSelector(
    (state: TRootState) => ({
      calendarDate: state.payouts.calendarDate,
      payouts: state.payouts.payouts,
      adminId: state.auth.adminId,
      filters: state.payouts.filters,
      customers: state.payouts.customers,
    }),
  );

  const [searchText, setSearchText] = useState("");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showPayoutView, setShowPayoutView] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [payoutSummary, setPayoutSummary] = useState({
    total: 0,
    noOfPayouts: 0,
    pendingTransactions: 0,
  });

  const dispatch = useDispatch();

  const { handleGetCall } = useAxiosClient();

  const calendarDateRef = useRef(calendarDate);

  const isDateChanged =
    calendarDateRef.current.fromDate != calendarDate.fromDate ||
    calendarDateRef.current.toDate != calendarDate.toDate ||
    calendarDateRef.current.mode != calendarDate.mode;

  useEffect(() => {
    // if (payouts.length <= 0 || isDateChanged) {
    _getPayouts();
    // }
    if (customers.length <= 0) {
      _getCustomers();
    }
    calendarDateRef.current = calendarDate;
  }, [calendarDate]);

  // For caculating the payouts summary card data
  // on search and filters
  useEffect(() => {
    _updatedPayoutSummary();
  }, [searchText, payouts.length, filters]);

  const _updatedPayoutSummary = () => {
    let updatedPayouts = getUpdatedPayouts(searchText, payouts, filters);
    let pendingTransactions = updatedPayouts.filter(
      (p) => p.status == "INITIATED",
    ).length;
    let total = updatedPayouts.reduce((t, p) => t + p.amount, 0);
    setPayoutSummary({
      total: total,
      noOfPayouts: updatedPayouts.length,
      pendingTransactions: pendingTransactions,
    });
  };

  const _getCustomers = () => {
    handleGetCall<CustomerProfile[]>(
      `payments/payouts/profile/adminId/${adminId}?type=P`,
    ).then((res) => {
      if (res.data != null) {
        dispatch(
          setCustomers({
            customers: mapCustomersResponseToModel(res.data),
          }),
        );
      }
    });
  };

  const _getPayouts = () => {
    handleGetCall<PayoutDataResponse[]>(
      `payments/payouts/adminId/${adminId}?fromDate=${calendarDate.fromDate}&toDate=${calendarDate.toDate}`,
    ).then((res) => {
      if (res.data != null) {
        dispatch(
          setPayouts({
            payouts: mapPayoutResponseToModel(res.data),
          }),
        );
      } else {
        dispatch(
          setPayouts({
            payouts: [],
          }),
        );
      }
    });
  };

  const onChangeDate = (fromDate: string, toDate: string, mode: string) => {
    dispatch(
      setCalendarDate({
        calendarDate: {
          fromDate: fromDate,
          toDate: toDate,
          mode: mode as TCalendarType,
        },
      }),
    );
  };

  const onRefresh = () => {
    _getPayouts();
  };

  let updatedPayouts = getUpdatedPayouts(searchText, payouts, filters);

  const onClickPayout = (index: number) => {
    let payoutId = updatedPayouts[index].payoutId;
    setShowPayoutView(true);
    dispatch(setPayoutId({ payoutId: payoutId }));
    dispatch(setPayoutViewId({ payoutId: payoutId, payoutType: "PAYOUT" }));
  };

  const isFilterApplied = () => {
    return (
      filters.selectedTypes.length > 0 ||
      filters.selectedStatuses.length > 0 ||
      filters.selectedCustomers.length > 0
    );
  };

  const handlePayoutsExport = () => {
    const headers = [
      "Trade Id / Loan Id",
      "UTR",
      "Created At",
      "Type",
      "Receiver",
      "Amount",
      "Payout Id",
      "Status",
    ];

    const data = payouts.map((item) => ({
      prodId: item.prodId,
      utr: item.utr,
      createdOn: item.createdOn,
      type: item.type,
      receiver: item.receiver,
      amount: item.amount,
      payoutId: item.payoutId,
      status: item.status,
    }));

    const exportData = [
      headers,
      ...data.map((row) => [
        row.prodId,
        row.utr,
        row.createdOn,
        row.type,
        row.receiver,
        row.amount,
        row.payoutId,
        row.status,
      ]),
    ];

    if (data.length > 0) {
      const worksheet = XLSX.utils.aoa_to_sheet(exportData);

      const totalRows = exportData.length;
      const totalCols = exportData[0]?.length || 0;

      worksheet["!rows"] = Array(totalRows).fill({ hpt: 22 });

      worksheet["!cols"] = Array(totalCols).fill({ wch: 18 });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Reports");
      XLSX.writeFile(
        workbook,
        getReportFileName({
          reportName: "Payouts",
          reportType: calendarDate.mode,
          fromDate: calendarDate.fromDate,
          toDate: calendarDate.toDate,
        }),
      );
    } else {
      toast.error("No data available to export excel.");
    }
  };
  return {
    searchText,
    setSearchText,
    calendarDate,
    payouts,
    onChangeDate,
    onRefresh,
    onClickPayout,
    payoutSummary,
    isCollapsed,
    setIsCollapsed,
    showPayoutView,
    setShowPayoutView,
    showFilters,
    setShowFilters,
    filters,
    customers,
    isFilterApplied,
    handlePayoutsExport,
  };
};

export default usePayouts;
